import type { Mission } from "@rogoag/mission";
import type { Job } from "@domain/CRM/job";
import type { CoreUpdateInput } from "@domain/MissionControl";
import type { IMissionLoader } from "@ports/missions/IMissionLoader";
import type { IMissionRuntime, MissionKernel } from "@ports/missions/IMissionRuntime";
import type { IMissions } from "@ports/missions/IMissions";
import type { IPathfinder } from "@ports/pathfinder/IPathfinder";

export interface MissionsUseCasesDeps {
	missionLoader: IMissionLoader;
	missionRuntime: IMissionRuntime;
	missionsApi: IMissions;
	pathfinder: IPathfinder;
}

/**
 * Bound mission use cases consumed by the GUI/CLI. The loader half delegates
 * to the injected `IMissionLoader` (whose adapter owns the per-Job caches);
 * the mutate half calls `IMissions`, whose endpoints re-run the job's exit
 * interview themselves before answering, so the job's "issues" status already
 * reflects the new core coordinates when the call resolves. `loadKernel`
 * resolves the mission runtime (`IMissionRuntime`, loaded once) for callers of
 * the package's free functions. Each method is independently testable
 * by importing the underlying function and passing a stub deps object — the
 * factory exists to bundle the deps once at composition time so consumers
 * see a single namespace.
 */
export interface MissionsUseCases {
	parse(bytes: Uint8Array): Promise<Mission>;
	loadKernel(): Promise<MissionKernel>;
	findPath: IPathfinder["findPath"];
	simulate: IPathfinder["simulate"];
	loadExe(job: Job): Promise<Mission | undefined>;
	loadInt(job: Job): Promise<Mission | undefined>;
	loadPtsShp(job: Job, expectedType?: string): Promise<Mission | "no-attachment" | "download-failed" | "parse-failed">;
	updateCores(jobId: string, inputs: CoreUpdateInput[]): Promise<void>;
	deleteCores(jobId: string, coreIds: string[]): Promise<void>;
}

/**
 * Submits a manual core-edit payload (pulled lat/lon + length) to the Missions
 * backend, which re-runs the exe-mission-validation pipeline. No-ops cleanly when
 * `inputs` is empty. Resolves once the re-run has finished; the caller owns the
 * cache invalidation + reselect.
 */
async function updateCores(deps: MissionsUseCasesDeps, jobId: string, inputs: CoreUpdateInput[]): Promise<void> {
	if (inputs.length === 0) return;
	await deps.missionsApi.updateCores(jobId, inputs);
}

/** Deletes a list of cores by id; the backend then re-runs exe-mission-validation. */
async function deleteCores(deps: MissionsUseCasesDeps, jobId: string, coreIds: string[]): Promise<void> {
	await deps.missionsApi.deleteCores(jobId, coreIds);
}

export function makeMissionsUseCases(deps: MissionsUseCasesDeps): MissionsUseCases {
	return {
		parse: (bytes) => deps.missionLoader.parse(bytes),
		loadKernel: () => deps.missionRuntime.load(),
		findPath: (request, options) => deps.pathfinder.findPath(request, options),
		simulate: (request) => deps.pathfinder.simulate(request),
		loadExe: (job) => deps.missionLoader.loadExe(job),
		loadInt: (job) => deps.missionLoader.loadInt(job),
		loadPtsShp: (job, expectedType) => deps.missionLoader.loadPtsShp(job, expectedType),
		updateCores: (jobId, inputs) => updateCores(deps, jobId, inputs),
		deleteCores: (jobId, coreIds) => deleteCores(deps, jobId, coreIds),
	};
}
